import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, MessageCircle, Clock, Loader2, Send } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/lib/motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";
import { BRAND, SERVICES, IMAGES } from "@/lib/content";
import { toast } from "sonner";

const waLink = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent("Hi Komel, I'd like to request a callback / consultation.")}`;

const initial = { full_name: "", phone: "", email: "", city: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const wordCount = form.message.trim() ? form.message.trim().split(/\s+/).length : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email || !form.message) {
      return toast.error("Please fill in your name, phone, email and message.");
    }
    if (wordCount > 50) return toast.error("Please keep your message under 50 words.");
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Enquiry received!");
      setForm(initial);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative z-10">
      <PageHeader
        overline="Let's Connect"
        titleLines={["Book Your", <span key="2" className="text-gold italic">Consultation</span>]}
        subtitle="Reach out for a complimentary initial consultation. Komel's team will respond promptly."
        image={IMAGES.skyline2}
      />

      <section className="py-10 md:py-16">
        <div className="mx-auto max-w-7xl px-6 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-6">
            <Reveal>
              {[
                { icon: Phone, label: "Phone", value: BRAND.phone, href: `tel:${BRAND.phoneRaw}` },
                { icon: Mail, label: "Email", value: BRAND.email, href: `mailto:${BRAND.email}` },
                { icon: MessageCircle, label: "WhatsApp", value: "Chat with Komel", href: waLink },
                { icon: MapPin, label: "Office", value: BRAND.address },
              ].map((c) => (
                <a key={c.label} href={c.href || undefined} target={c.href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                  className="flex items-center gap-4 glass rounded-2xl p-5 mb-4 transition-transform duration-300 hover:-translate-y-0.5" data-testid={`contact-info-${c.label.toLowerCase()}`}>
                  <span className="grid place-items-center h-12 w-12 rounded-xl bg-primary-green/15 text-primary-green shrink-0"><c.icon size={20} /></span>
                  <div><div className="text-xs text-[#94A3B8] uppercase tracking-wide">{c.label}</div><div className="text-white font-medium">{c.value}</div></div>
                </a>
              ))}
            </Reveal>
            <Reveal delay={0.1}>
              <div className="glass rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4"><Clock size={18} className="text-gold" /><h4 className="font-display text-xl">Business Hours</h4></div>
                <ul className="space-y-2 text-[#94A3B8] text-sm">
                  <li className="flex justify-between"><span>Monday – Friday</span><span className="text-white">9:00 AM – 7:00 PM</span></li>
                  <li className="flex justify-between"><span>Saturday</span><span className="text-white">10:00 AM – 5:00 PM</span></li>
                  <li className="flex justify-between"><span>Sunday</span><span className="text-white">By Appointment</span></li>
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="overflow-hidden rounded-2xl border border-white/10 h-64">
                <iframe title="Dubai Map" data-testid="contact-map" className="w-full h-full grayscale contrast-125 opacity-90"
                  src="https://www.google.com/maps?q=Dubai,UAE&output=embed" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <form onSubmit={submit} className="glass rounded-3xl p-8 md:p-10 space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-2 block">Full Name *</label>
                    <Input data-testid="contact-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="bg-white/5 border-white/15 h-12 text-white placeholder:text-[#64748B]" placeholder="Your name" />
                  </div>
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-2 block">Phone Number *</label>
                    <Input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white/5 border-white/15 h-12 text-white placeholder:text-[#64748B]" placeholder="+971 ..." />
                  </div>
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-2 block">Email *</label>
                    <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/5 border-white/15 h-12 text-white placeholder:text-[#64748B]" placeholder="you@email.com" />
                  </div>
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-2 block">City</label>
                    <Input data-testid="contact-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-white/5 border-white/15 h-12 text-white placeholder:text-[#64748B]" placeholder="Dubai" />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-[#94A3B8] mb-2 block">Service Interested In</label>
                  <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                    <SelectTrigger data-testid="contact-service" className="bg-white/5 border-white/15 h-12 text-white"><SelectValue placeholder="Select a service" /></SelectTrigger>
                    <SelectContent className="glass border-white/10 text-white">
                      {SERVICES.map((s) => <SelectItem key={s.key} value={s.title}>{s.title}</SelectItem>)}
                      <SelectItem value="Request a Callback">Request a Callback</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm text-[#94A3B8]">Message * <span className="text-[#64748B]">(up to 50 words)</span></label>
                    <span className={`text-xs ${wordCount > 50 ? "text-red-400" : "text-[#64748B]"}`}>{wordCount}/50</span>
                  </div>
                  <Textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} className="bg-white/5 border-white/15 text-white placeholder:text-[#64748B] resize-none" placeholder="Tell us about your space and how we can help..." />
                </div>
                <button type="submit" disabled={loading} data-testid="contact-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary-green h-13 py-4 font-medium text-white transition-all duration-300 hover:bg-[#059669] disabled:opacity-60">
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <>Send Enquiry <Send size={16} /></>}
                </button>
                <p className="text-center text-xs text-[#64748B]">Or <a href={waLink} target="_blank" rel="noreferrer" className="text-gold link-underline">message us on WhatsApp</a> for an instant response.</p>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
